import { isObject } from '@sveltia/utils/object';

import { getMediaLibraryOptions } from '$lib/services/integrations/media-libraries';
import {
  RASTER_IMAGE_EXTENSION_REGEX,
  RASTER_IMAGE_FORMATS,
} from '$lib/services/utils/media/image';
import { optimizeSVG, transformImage } from '$lib/services/utils/media/image/transform';

/**
 * @import {
 * DefaultMediaLibraryConfig,
 * FileTransformations,
 * MediaField,
 * RasterImageTransformationOptions,
 * } from '$lib/types/public';
 */

/**
 * Get normalized default media library options.
 * @param {object} [options] Options.
 * @param {MediaField} [options.fieldConfig] Field configuration.
 * @returns {{ enabled: boolean, config: DefaultMediaLibraryConfig }} Options.
 */
export const getDefaultMediaLibraryOptions = ({ fieldConfig } = {}) => {
  const options = getMediaLibraryOptions({ fieldConfig });

  /** @type {DefaultMediaLibraryConfig} */
  const {
    max_file_size: maxSize,
    multiple,
    slugify_filename: slugify,
    transformations,
  } = typeof options === 'boolean' ? {} : (options?.config ?? {});

  return {
    enabled: options !== false,
    config: {
      max_file_size: typeof maxSize === 'number' && Number.isInteger(maxSize) ? maxSize : Infinity,
      multiple: typeof multiple === 'boolean' ? multiple : false,
      slugify_filename: typeof slugify === 'boolean' ? slugify : false,
      transformations: isObject(transformations) ? transformations : undefined,
    },
  };
};

/**
 * Process the given file by applying a transformation if available.
 * @param {File} file Original file.
 * @param {FileTransformations} transformations File transformation options.
 * @returns {Promise<File>} Transformed file, or the original file if no transformation is applied
 * or the transformation fails. The options are validated by the config parser, so they are used as
 * is here.
 */
export const transformFile = async (file, transformations) => {
  const [type, subType] = file.type.split('/');

  // Process raster image
  if (type === 'image' && subType !== 'svg+xml') {
    /** @type {RasterImageTransformationOptions | undefined} */
    let transformation;

    if (/** @type {string[]} */ (RASTER_IMAGE_FORMATS).includes(subType)) {
      if (subType in transformations) {
        transformation = /** @type {Record<string, any>} */ (transformations)[subType];
      } else if ('raster_image' in transformations) {
        transformation = transformations.raster_image;
      }
    }

    if (transformation) {
      const { format = 'webp', quality = 85, width, height } = transformation;
      /** @type {Blob} */
      let blob;

      try {
        blob = await transformImage(file, { format, quality, width, height });
      } catch {
        // The browser can’t decode the file — a HEIC image saved with a `.jpg` extension, say — so
        // upload it as is instead of failing the whole selection. This mirrors `optimizeSVG`, which
        // also falls back to the original blob.
        return file;
      }

      const newFileName =
        blob.type === `image/${format}`
          ? RASTER_IMAGE_EXTENSION_REGEX.test(file.name)
            ? file.name.replace(RASTER_IMAGE_EXTENSION_REGEX, format)
            : file.name.concat(format)
          : // Failed to transform
            file.name;

      return new File([blob], newFileName, { type: blob.type });
    }
  }

  // Process SVG image
  if (type === 'image' && subType === 'svg+xml' && transformations.svg?.optimize) {
    return new File([await optimizeSVG(file)], file.name, { type: file.type });
  }

  return file;
};
